// Antrian pelanggan — layar petugas (brief owner 30 Agu 2026 poin 1).
//
// SATU HALAMAN, EMPAT PERAN — dipisah tab dan digerbangi izin yang SAMA
// dengan backend (`routers/antrian.py`), supaya tidak ada tombol yang
// ujungnya 403 (CLAUDE.md #3):
//   Ambil Nomor (antrian.create)  — usher/kiosk mencetak tiket thermal.
//   Layani      (antrian.create)  — FL memindai barcode lalu memilih hasil.
//   Antrean     (view ATAU create)— papan antrean hari ini + tombol lepas SPV.
//   Rekap       (loss_customer.view) — konversi membeli vs tidak membeli per sales.
//   Pengaturan  (antrian.update)  — saklar gerbang keras + tautan layar tablet.
//
// Pemindainya memakai `components/hris/BarcodeScanner.jsx` yang SUDAH ADA
// (dua mesin baca: BarcodeDetector + ZXing untuk iOS) — bukan pemindai kedua.
// Kolom ketik manual TETAP disediakan berdampingan: kamera bisa mati, tiket
// bisa kusut, dan pelanggan tidak boleh menunggu gara-gara itu.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Ticket, ScanLine, ShoppingBag, UserX, UserMinus, Unlock, Settings,
  BarChart3, Monitor, RefreshCw, Printer, ListOrdered, AlertTriangle,
  FilePlus, ShieldCheck, Save, KeyRound, Search,
} from "lucide-react";
import BarcodeScanner from "../components/hris/BarcodeScanner";
import TiketPrint from "../components/antrian/TiketPrint";
import LoadingState from "@/components/ui/loading-state";
import { MonthPicker } from "@/components/ui/date-picker";

const pesanGalat = (e, bawaan) => e?.response?.data?.detail || e?.message || bawaan;

const formKosong = () => ({
  nama_pelanggan: "", no_hp: "", alasan_kode: "", alasan_catatan: "", produk_diminati: "",
});

/** Slug untuk data-testid per jabatan — nama jabatan berisi spasi/huruf besar
 *  ("Sales Leasing"), dan testid yang berspasi tidak bisa dipakai selector. */
const slugJabatan = (s) => String(s || "").toLowerCase().trim()
  .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

export default function Antrian() {
  const { can, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const bolehLayani = can("antrian", "create");
  const bolehLihat = can("antrian", "view");
  const bolehAtur = can("antrian", "update");
  const bolehRekap = can("loss_customer", "view");
  // Syarat yang SAMA dengan menu Input Nota — kalau berbeda, tombol "Buat
  // nota" akan mengantar ke halaman yang menolak pengguna itu.
  const bolehBuatNota = can("nota_sales", "view");
  // Daftar wajib scan hanya boleh DIUBAH superadmin/admin (keputusan owner 3),
  // dan backend menegakkan ROLE, bukan izin modul — jadi gerbangnya role juga.
  const bolehUbahWajibScan = ["superadmin", "admin"].includes(user?.role);
  const bolehBeriIzinScan = user?.role === "superadmin";

  // ?tab=<kunci> dipakai Input Nota untuk mengantar FL langsung ke tab Layani.
  // Hanya dituruti kalau tab itu memang boleh dilihat — kalau tidak, pengguna
  // mendarat di tab kosong yang tidak ada di barisan tombol tab.
  const tabDiminta = searchParams.get("tab");
  const tabBoleh = useMemo(() => ({
    layani: bolehLayani, ambil: bolehLayani, antrean: bolehLayani || bolehLihat,
    rekap: bolehRekap, pengaturan: bolehAtur,
  }), [bolehLayani, bolehLihat, bolehRekap, bolehAtur]);
  const [tab, setTab] = useState(() => (tabDiminta && tabBoleh[tabDiminta])
    ? tabDiminta
    : (bolehLayani ? "layani" : (bolehLihat ? "antrean" : "rekap")));
  useEffect(() => {
    // Navigasi ke /antrian?tab=... saat halaman ini SUDAH terbuka tidak
    // me-mount ulang komponen, jadi initializer useState di atas tidak jalan.
    if (tabDiminta && tabBoleh[tabDiminta]) setTab(tabDiminta);
  }, [tabDiminta, tabBoleh]);
  const [branches, setBranches] = useState([]);
  const [cabang, setCabang] = useState("");
  const [pengaturan, setPengaturan] = useState(null);

  // Ambil nomor / cetak
  const [tiketBaru, setTiketBaru] = useState(null);
  const [busyAmbil, setBusyAmbil] = useState(false);

  // Layani
  const [tiketSaya, setTiketSaya] = useState(null);
  // Tiket yang sudah ditutup "membeli" hari ini oleh FL ini. Satu tiket boleh
  // untuk lebih dari satu nota (keputusan owner 2), jadi FL butuh jalan balik
  // ke Input Nota untuk tiket yang SUDAH selesai, bukan cuma yang sedang dipegang.
  const [membeliHariIni, setMembeliHariIni] = useState([]);
  const [gerbangAktif, setGerbangAktif] = useState(false);
  const [kodeKetik, setKodeKetik] = useState("");
  const [busyLayani, setBusyLayani] = useState(false);
  const [formTidak, setFormTidak] = useState(null);
  const [alasanOpsi, setAlasanOpsi] = useState([]);

  // Antrean
  const [antrean, setAntrean] = useState(null);
  const [loadingAntrean, setLoadingAntrean] = useState(false);

  // Rekap
  const [rekap, setRekap] = useState(null);
  const [loadingRekap, setLoadingRekap] = useState(false);
  const [rekapPeriode, setRekapPeriode] = useState("");

  // ---- Data awal ----
  useEffect(() => {
    api.get("/finance/branches/master")
      .then((r) => {
        const nama = (r.data.items || []).map((b) => b.name).filter(Boolean);
        setBranches(nama);
        setCabang((lama) => lama || nama[0] || "");
      })
      .catch(() => {});
    // Daftar alasan datang dari modul Loss Customer yang SUDAH ADA — owner
    // mengelolanya di halaman Loss Customer, bukan di sini (satu sumber).
    api.get("/loss-customer/alasan-opsi")
      .then((r) => setAlasanOpsi(r.data.items || []))
      .catch(() => {});
  }, []);

  const muatPengaturan = useCallback(() => {
    if (!bolehLihat) return;
    api.get("/antrian/pengaturan").then((r) => setPengaturan(r.data)).catch(() => {});
  }, [bolehLihat]);
  useEffect(() => { muatPengaturan(); }, [muatPengaturan]);

  const muatTiketSaya = useCallback(() => {
    if (!bolehLayani && !bolehLihat) return;
    api.get("/antrian/tiket-saya")
      .then((r) => {
        setTiketSaya(r.data.tiket || null);
        setGerbangAktif(!!r.data.gerbang_antrian_aktif);
        setMembeliHariIni(Array.isArray(r.data.tiket_membeli_hari_ini) ? r.data.tiket_membeli_hari_ini : []);
      })
      .catch(() => {});
  }, [bolehLayani, bolehLihat]);
  useEffect(() => { muatTiketSaya(); }, [muatTiketSaya]);

  const muatAntrean = useCallback((c) => {
    if (!bolehLayani && !bolehLihat) return;
    setLoadingAntrean(true);
    api.get("/antrian/antrean", { params: c ? { cabang: c } : {} })
      .then((r) => setAntrean(r.data))
      .catch((e) => toast.error(pesanGalat(e, "Gagal memuat antrean")))
      .finally(() => setLoadingAntrean(false));
  }, [bolehLayani, bolehLihat]);

  useEffect(() => {
    if (tab !== "antrean") return;
    muatAntrean(cabang);
    // Papan antrean disegarkan berkala supaya petugas tidak perlu menekan
    // muat ulang setiap kali ada nomor baru.
    const t = setInterval(() => muatAntrean(cabang), 15000);
    return () => clearInterval(t);
  }, [tab, cabang, muatAntrean]);

  // ---- Aksi ----
  const ambilNomor = async () => {
    if (!cabang) { toast.error("Pilih cabang dulu"); return; }
    setBusyAmbil(true);
    try {
      const { data } = await api.post("/antrian", { cabang });
      setTiketBaru({ ...data.item, _antrean_di_depan: data.antrean_di_depan,
                     _cetak: data.pengaturan_cetak });
      toast.success(data.message || "Nomor antrian terbit");
    } catch (e) {
      toast.error(pesanGalat(e, "Gagal menerbitkan nomor antrian"));
    } finally {
      setBusyAmbil(false);
    }
  };

  // Penanda "tiket dicetak" untuk laporan antrian di Dashboard (brief owner
  // 15 Sep 2026, G7). Gagalnya TIDAK boleh menghalangi pelanggan mendapat
  // tiket — dialog cetaknya sudah terbuka — tapi juga tidak boleh senyap:
  // angka "dicetak" di dashboard jadi kurang satu tanpa ada yang tahu.
  const catatCetak = async (t) => {
    if (!t?.id) return;
    try {
      await api.post(`/antrian/${t.id}/dicetak`);
    } catch (e) {
      toast.warning(`Tiket tetap bisa dicetak, tapi catatan cetaknya gagal tersimpan: ${pesanGalat(e, "galat jaringan")}`);
    }
  };

  const panggil = async (kode) => {
    const bersih = (kode || "").trim();
    if (!bersih) { toast.error("Kode antrian kosong"); return; }
    setBusyLayani(true);
    try {
      const { data } = await api.post("/antrian/panggil", { kode_antrian: bersih });
      setTiketSaya(data.item);
      setKodeKetik("");
      toast.success(data.message || "Tiket terkunci");
    } catch (e) {
      toast.error(pesanGalat(e, "Gagal memanggil tiket"));
    } finally {
      setBusyLayani(false);
    }
  };

  const selesaikan = async (hasil, extra = {}) => {
    if (!tiketSaya) return;
    setBusyLayani(true);
    try {
      const { data } = await api.post("/antrian/selesai", {
        tiket_id: tiketSaya.id, hasil, ...extra,
      });
      toast.success(data.message || "Tiket selesai");
      setFormTidak(null);
      setTiketSaya(null);
      if (hasil === "membeli") {
        const kode = data?.item?.kode_antrian;
        // Tiketnya TETAP ditutup dulu di atas (rekap konversi tidak boleh
        // bergantung pada nota jadi dibuat atau tidak), baru FL diantar ke
        // Input Nota dengan kodenya — supaya kode tidak perlu diketik ulang.
        if (kode && bolehBuatNota) {
          navigate(`/input-nota?kode_antrian=${encodeURIComponent(kode)}`);
          return;
        }
        toast.info(`Sertakan kode antrian ${kode} saat membuat nota`);
      }
      muatTiketSaya();
      muatAntrean(cabang);
    } catch (e) {
      toast.error(pesanGalat(e, "Gagal menyelesaikan tiket"));
      // Muat ulang: kalau gagal karena tiketnya sudah dilepas supervisor,
      // kartu "sedang melayani" harus ikut hilang, bukan tinggal di layar.
      muatTiketSaya();
    } finally {
      setBusyLayani(false);
    }
  };

  const lepas = async (t) => {
    const alasan = window.prompt(
      `Lepas tiket ${t.nomor_antrian} dari ${t.fl_nama || "-"}?\nTulis alasannya (tercatat sebagai jejak audit):`);
    if (alasan === null) return;
    try {
      const { data } = await api.post(`/antrian/${t.id}/lepas`, { alasan });
      toast.success(data.message || "Tiket dilepas");
      muatAntrean(cabang);
      muatTiketSaya();
    } catch (e) {
      toast.error(pesanGalat(e, "Gagal melepas tiket"));
    }
  };

  const simpanPengaturan = async (patch) => {
    try {
      const { data } = await api.put("/antrian/pengaturan", patch);
      toast.success(data.message || "Tersimpan");
      muatPengaturan();
      muatTiketSaya();
    } catch (e) {
      toast.error(pesanGalat(e, "Gagal menyimpan pengaturan"));
    }
  };

  const buatToken = async (putar) => {
    if (!cabang) { toast.error("Pilih cabang dulu"); return; }
    try {
      const { data } = await api.post("/antrian/display-token", { cabang, putar });
      toast.success(`Tautan layar tablet ${cabang} siap`);
      muatPengaturan();
      return data;
    } catch (e) {
      toast.error(pesanGalat(e, "Gagal membuat tautan layar"));
      return null;
    }
  };

  const muatRekap = useCallback(() => {
    if (!bolehRekap) return;
    setLoadingRekap(true);
    api.get("/loss-customer/rekap", { params: rekapPeriode ? { periode: rekapPeriode } : {} })
      .then((r) => setRekap(r.data))
      .catch((e) => toast.error(pesanGalat(e, "Gagal memuat rekap")))
      .finally(() => setLoadingRekap(false));
  }, [bolehRekap, rekapPeriode]);
  useEffect(() => { if (tab === "rekap") muatRekap(); }, [tab, muatRekap]);

  if (!bolehLayani && !bolehLihat && !bolehRekap) {
    return <div className="p-6 text-sm text-slate-600">Anda tidak punya akses ke modul Antrian.</div>;
  }

  const tabs = [
    bolehLayani && { id: "layani", label: "Layani", ikon: ScanLine },
    bolehLayani && { id: "ambil", label: "Ambil Nomor", ikon: Ticket },
    (bolehLayani || bolehLihat) && { id: "antrean", label: "Antrean", ikon: ListOrdered },
    bolehRekap && { id: "rekap", label: "Rekap Konversi", ikon: BarChart3 },
    bolehAtur && { id: "pengaturan", label: "Pengaturan", ikon: Settings },
  ].filter(Boolean);

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Ticket className="w-5 h-5" /> Antrian Pelanggan
          </h1>
          <p className="text-sm text-slate-500">
            Nomor antrian ber-barcode, panggilan oleh FL, dan pencatatan pelanggan yang tidak jadi membeli.
          </p>
        </div>
        <select
          value={cabang} onChange={(e) => setCabang(e.target.value)}
          data-testid="antrian-pilih-cabang"
          className="h-9 px-3 rounded-lg border border-slate-300 text-sm"
        >
          <option value="">— pilih cabang —</option>
          {branches.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
      </div>

      {gerbangAktif && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
             data-testid="antrian-banner-gerbang">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            Gerbang antrian <b>AKTIF</b>: nota baru ditolak selama Anda masih memegang tiket yang belum
            diselesaikan. Kalau tiket nyangkut, minta supervisor menekan “Lepas” di tab Antrean.
          </span>
        </div>
      )}

      <div className="flex flex-wrap gap-1 border-b border-slate-200">
        {tabs.map((t) => (
          <button
            key={t.id} type="button"
            onClick={() => {
              setTab(t.id);
              // Buang ?tab supaya tautan /antrian?tab=layani berikutnya tetap
              // dianggap perubahan dan benar-benar memindah tab.
              if (tabDiminta) {
                setSearchParams((p) => { p.delete("tab"); return p; }, { replace: true });
              }
            }}
            data-testid={`antrian-tab-${t.id}`}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm border-b-2 -mb-px ${
              tab === t.id ? "border-slate-900 text-slate-900 font-medium"
                           : "border-transparent text-slate-500 hover:text-slate-700"}`}
          >
            <t.ikon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {/* ================= LAYANI (FL) ================= */}
      {tab === "layani" && (
        <div className="space-y-4">
          {!tiketSaya ? (
            <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
              <p className="text-sm text-slate-700">
                Pindai barcode tiket pelanggan, atau ketik kodenya kalau barcode tidak terbaca.
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <BarcodeScanner
                  onDetected={(kode) => panggil(kode)}
                  testId="antrian-btn-scan"
                  title="Scan barcode tiket antrian"
                />
                <input
                  value={kodeKetik} onChange={(e) => setKodeKetik(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") panggil(kodeKetik); }}
                  placeholder="atau ketik kode tiket (mis. ANT-CABANG-20260831-001)"
                  data-testid="antrian-input-kode"
                  className="h-9 px-3 rounded-lg border border-slate-300 text-sm flex-1 min-w-[240px]"
                />
                <button
                  type="button" onClick={() => panggil(kodeKetik)} disabled={busyLayani}
                  data-testid="antrian-btn-panggil"
                  className="h-9 px-4 rounded-lg bg-slate-900 text-white text-sm disabled:opacity-50"
                >
                  Panggil
                </button>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 space-y-3"
                 data-testid="antrian-kartu-melayani">
              <p className="text-sm text-emerald-900">Sedang melayani</p>
              <p className="text-4xl font-bold text-emerald-900" data-testid="antrian-nomor-dilayani">
                {tiketSaya.nomor_antrian}
              </p>
              <p className="text-xs text-emerald-800 break-all">{tiketSaya.kode_antrian} · {tiketSaya.cabang}</p>

              {!formTidak ? (
                <div className="flex flex-wrap gap-2 pt-1">
                  <button
                    type="button" onClick={() => selesaikan("membeli")} disabled={busyLayani}
                    data-testid="antrian-btn-membeli"
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-emerald-600 text-white text-sm disabled:opacity-50"
                  >
                    <ShoppingBag className="w-4 h-4" /> Membeli
                  </button>
                  <button
                    type="button" onClick={() => setFormTidak(formKosong())} disabled={busyLayani}
                    data-testid="antrian-btn-tidak-membeli"
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-rose-600 text-white text-sm disabled:opacity-50"
                  >
                    <UserX className="w-4 h-4" /> Tidak Membeli
                  </button>
                  <button
                    type="button" onClick={() => selesaikan("tidak_hadir")} disabled={busyLayani}
                    data-testid="antrian-btn-tidak-hadir"
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-slate-300 bg-white text-slate-700 text-sm disabled:opacity-50"
                  >
                    <UserMinus className="w-4 h-4" /> Tidak hadir
                  </button>
                </div>
              ) : (
                <form
                  className="space-y-2 bg-white rounded-lg p-3 border border-slate-200"
                  data-testid="antrian-form-tidak-membeli"
                  onSubmit={(e) => { e.preventDefault(); selesaikan("tidak_membeli", formTidak); }}
                >
                  <p className="text-sm font-medium text-slate-800">
                    Data pelanggan yang tidak jadi membeli — wajib diisi
                  </p>
                  <input
                    value={formTidak.nama_pelanggan} required
                    onChange={(e) => setFormTidak({ ...formTidak, nama_pelanggan: e.target.value })}
                    placeholder="Nama pelanggan *" data-testid="antrian-form-nama"
                    className="w-full h-9 px-3 rounded-lg border border-slate-300 text-sm"
                  />
                  <input
                    value={formTidak.no_hp} required
                    onChange={(e) => setFormTidak({ ...formTidak, no_hp: e.target.value })}
                    placeholder="Nomor HP *" data-testid="antrian-form-hp"
                    className="w-full h-9 px-3 rounded-lg border border-slate-300 text-sm"
                  />
                  <select
                    value={formTidak.alasan_kode} required
                    onChange={(e) => setFormTidak({ ...formTidak, alasan_kode: e.target.value })}
                    data-testid="antrian-form-alasan"
                    className="w-full h-9 px-3 rounded-lg border border-slate-300 text-sm"
                  >
                    <option value="">— alasan tidak jadi membeli * —</option>
                    {alasanOpsi.map((a) => <option key={a.kode} value={a.kode}>{a.label}</option>)}
                  </select>
                  <input
                    value={formTidak.alasan_catatan}
                    onChange={(e) => setFormTidak({ ...formTidak, alasan_catatan: e.target.value })}
                    placeholder={formTidak.alasan_kode === "lainnya"
                      ? "Catatan alasan (wajib untuk 'Lainnya')" : "Catatan tambahan (opsional)"}
                    required={formTidak.alasan_kode === "lainnya"}
                    data-testid="antrian-form-catatan"
                    className="w-full h-9 px-3 rounded-lg border border-slate-300 text-sm"
                  />
                  <input
                    value={formTidak.produk_diminati}
                    onChange={(e) => setFormTidak({ ...formTidak, produk_diminati: e.target.value })}
                    placeholder="Produk yang dicari (opsional)" data-testid="antrian-form-produk"
                    className="w-full h-9 px-3 rounded-lg border border-slate-300 text-sm"
                  />
                  <div className="flex gap-2 pt-1">
                    <button
                      type="submit" disabled={busyLayani} data-testid="antrian-form-simpan"
                      className="h-9 px-4 rounded-lg bg-rose-600 text-white text-sm disabled:opacity-50"
                    >
                      Simpan &amp; selesaikan tiket
                    </button>
                    <button
                      type="button" onClick={() => setFormTidak(null)} data-testid="antrian-form-batal"
                      className="h-9 px-4 rounded-lg border border-slate-300 text-sm text-slate-700"
                    >
                      Kembali
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          {membeliHariIni.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-2"
                 data-testid="antrian-membeli-hari-ini">
              <p className="text-sm font-medium text-slate-800">Tiket membeli hari ini</p>
              <ul className="divide-y divide-slate-100">
                {membeliHariIni.map((t) => (
                  <li key={t.id || t.kode_antrian}
                      className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                    <span className="min-w-0">
                      <b>{t.nomor_antrian}</b>
                      <span className="text-xs text-slate-500 ml-2 break-all">
                        {t.kode_antrian} · {t.cabang}
                      </span>
                      <span className="block text-xs text-slate-500">
                        {Number(t.jumlah_nota) || 0} nota tercatat
                      </span>
                    </span>
                    {bolehBuatNota && (
                      <button
                        type="button"
                        onClick={() => navigate(`/input-nota?kode_antrian=${encodeURIComponent(t.kode_antrian)}`)}
                        data-testid={`antrian-buat-nota-${t.kode_antrian}`}
                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-emerald-600 text-white text-sm"
                      >
                        <FilePlus className="w-4 h-4" /> Buat nota
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* ================= AMBIL NOMOR (usher/kiosk) ================= */}
      {tab === "ambil" && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
          <p className="text-sm text-slate-700">
            Terbitkan nomor antrian untuk pelanggan yang baru datang, lalu cetak tiketnya
            di printer thermal.
          </p>
          <button
            type="button" onClick={ambilNomor} disabled={busyAmbil || !cabang}
            data-testid="antrian-btn-ambil-nomor"
            className="inline-flex items-center gap-2 h-11 px-6 rounded-lg bg-slate-900 text-white text-base disabled:opacity-50"
          >
            <Printer className="w-5 h-5" /> Ambil &amp; Cetak Nomor
          </button>
        </div>
      )}

      {/* ================= ANTREAN ================= */}
      {tab === "antrean" && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <button
              type="button" onClick={() => muatAntrean(cabang)} data-testid="antrian-btn-muat-antrean"
              className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-slate-300 text-sm text-slate-700"
            >
              <RefreshCw className="w-4 h-4" /> Muat ulang
            </button>
            <span className="text-xs text-slate-500">disegarkan otomatis tiap 15 detik</span>
          </div>
          {loadingAntrean && !antrean ? <LoadingState /> : null}
          {antrean && (
            <div className="grid gap-3 md:grid-cols-3">
              <PapanAntrean judul={`Menunggu (${antrean.jumlah_menunggu})`} warna="slate"
                            items={antrean.menunggu} testId="antrian-kolom-menunggu" />
              <PapanAntrean judul={`Sedang dilayani (${antrean.jumlah_dipanggil})`} warna="emerald"
                            items={antrean.dipanggil} testId="antrian-kolom-dipanggil"
                            aksi={bolehAtur ? lepas : null} />
              <PapanAntrean judul={`Selesai (${antrean.jumlah_selesai})`} warna="slate"
                            items={antrean.selesai} testId="antrian-kolom-selesai" />
            </div>
          )}
        </div>
      )}

      {/* ================= REKAP ================= */}
      {tab === "rekap" && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <MonthPicker value={rekapPeriode} onChange={setRekapPeriode} placeholder="Semua periode" />
            <button
              type="button" onClick={muatRekap} data-testid="antrian-btn-muat-rekap"
              className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-slate-300 text-sm text-slate-700"
            >
              <RefreshCw className="w-4 h-4" /> Muat ulang
            </button>
          </div>
          {loadingRekap && !rekap ? <LoadingState /> : null}
          {rekap && !rekap.konversi_antrian && (
            <p className="text-sm text-slate-500" data-testid="antrian-rekap-tidak-tersedia">
              Data konversi antrian belum tersedia di server ini.
            </p>
          )}
          {rekap?.konversi_antrian && (
            <div className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-4">
                <Kartu label="Dilayani" nilai={rekap.konversi_antrian.total_dilayani} testId="rekap-dilayani" />
                <Kartu label="Membeli" nilai={rekap.konversi_antrian.total_membeli} testId="rekap-membeli" />
                <Kartu label="Tidak membeli" nilai={rekap.konversi_antrian.total_tidak_membeli} testId="rekap-tidak-membeli" />
                <Kartu label="Konversi" nilai={`${rekap.konversi_antrian.konversi_persen}%`} testId="rekap-konversi" />
              </div>
              <p className="text-xs text-slate-500">
                Tidak hadir ({rekap.konversi_antrian.total_tidak_hadir}) dan tiket yang dilepas supervisor
                ({rekap.konversi_antrian.total_dilepas_supervisor}) TIDAK dihitung sebagai kegagalan sales.
              </p>
              <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="text-left px-3 py-2">Sales / FL</th>
                      <th className="text-right px-3 py-2">Dilayani</th>
                      <th className="text-right px-3 py-2">Membeli</th>
                      <th className="text-right px-3 py-2">Tidak membeli</th>
                      <th className="text-right px-3 py-2">Tidak hadir</th>
                      <th className="text-right px-3 py-2">Konversi</th>
                    </tr>
                  </thead>
                  <tbody data-testid="antrian-tabel-per-sales">
                    {rekap.konversi_antrian.per_sales.length === 0 ? (
                      <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400">Belum ada data</td></tr>
                    ) : rekap.konversi_antrian.per_sales.map((r) => (
                      <tr key={r.sales} className="border-t border-slate-100">
                        <td className="px-3 py-2">{r.sales}</td>
                        <td className="px-3 py-2 text-right">{r.dilayani}</td>
                        <td className="px-3 py-2 text-right text-emerald-700">{r.membeli}</td>
                        <td className="px-3 py-2 text-right text-rose-700">{r.tidak_membeli}</td>
                        <td className="px-3 py-2 text-right text-slate-500">{r.tidak_hadir}</td>
                        <td className="px-3 py-2 text-right font-medium">{r.konversi_persen}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ================= PENGATURAN ================= */}
      {tab === "pengaturan" && pengaturan && (
        <div className="space-y-4">
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 space-y-2">
            <p className="text-sm font-medium text-rose-900 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Gerbang keras — hati-hati
            </p>
            <p className="text-sm text-rose-800">
              Kalau dinyalakan, FL <b>tidak bisa membuat nota baru</b> selama masih memegang tiket yang
              belum diselesaikan. Nyalakan hanya kalau semua FL sudah terbiasa dengan alur antrian, dan
              pastikan ada supervisor yang bisa melepas tiket nyangkut.
            </p>
            <label className="flex items-center gap-2 text-sm text-rose-900">
              <input
                type="checkbox" checked={!!pengaturan.gerbang_antrian_aktif}
                data-testid="antrian-toggle-gerbang"
                onChange={(e) => simpanPengaturan({ gerbang_antrian_aktif: e.target.checked })}
              />
              Aktifkan gerbang antrian
            </label>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
            <p className="text-sm font-medium text-slate-800">Tiket thermal</p>
            <label className="block text-sm text-slate-700">
              Lebar kertas
              <select
                value={pengaturan.lebar_kertas_mm} data-testid="antrian-lebar-kertas"
                onChange={(e) => simpanPengaturan({ lebar_kertas_mm: Number(e.target.value) })}
                className="ml-2 h-9 px-3 rounded-lg border border-slate-300 text-sm"
              >
                {(pengaturan.lebar_kertas_opsi || [58, 80]).map((w) => (
                  <option key={w} value={w}>{w} mm</option>
                ))}
              </select>
            </label>
            <BarisTeks label="Judul tiket" nilai={pengaturan.judul_tiket} testId="antrian-judul-tiket"
                       onSimpan={(v) => simpanPengaturan({ judul_tiket: v })} />
            <BarisTeks label="Teks penutup" nilai={pengaturan.teks_footer_tiket} testId="antrian-footer-tiket"
                       onSimpan={(v) => simpanPengaturan({ teks_footer_tiket: v })} />
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
            <p className="text-sm font-medium text-slate-800 flex items-center gap-2">
              <Monitor className="w-4 h-4" /> Layar tablet (publik, tanpa login)
            </p>
            <p className="text-sm text-slate-600">
              Buka tautan ini di tablet yang dipasang di ruang tunggu. Halamannya hanya menampilkan
              nomor — tidak bisa membuat atau mengubah apa pun.
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button" onClick={() => buatToken(false)} data-testid="antrian-btn-token"
                className="h-9 px-4 rounded-lg bg-slate-900 text-white text-sm"
              >
                Buat / tampilkan tautan {cabang ? `(${cabang})` : ""}
              </button>
              <button
                type="button" onClick={() => buatToken(true)} data-testid="antrian-btn-token-putar"
                className="inline-flex items-center gap-2 h-9 px-4 rounded-lg border border-slate-300 text-sm text-slate-700"
              >
                <Unlock className="w-4 h-4" /> Ganti tautan (yang lama langsung mati)
              </button>
            </div>
            <ul className="text-xs text-slate-600 space-y-1" data-testid="antrian-daftar-token">
              {Object.entries(pengaturan.display_tokens || {}).map(([c, t]) => (
                <li key={c} className="break-all">
                  <b>{c}</b>: {window.location.origin}/antrian-display/{t}
                </li>
              ))}
            </ul>
          </div>

          <KartuWajibScan
            pengaturan={pengaturan}
            bolehUbah={bolehUbahWajibScan}
            bolehBeriIzin={bolehBeriIzinScan}
            alasanOpsi={alasanOpsi}
            bolehAturAlasan={can("loss_customer", "update")}
            onTersimpan={() => { muatPengaturan(); muatTiketSaya(); }}
          />
        </div>
      )}

      {/* ================= Modal cetak tiket ================= */}
      {tiketBaru && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-auto p-4"
             data-testid="antrian-modal-cetak">
          <div className="bg-white rounded-2xl p-4 mt-8 max-w-md w-full">
            <div className="no-print flex items-center justify-between mb-3">
              <p className="font-medium text-slate-900">Tiket antrian</p>
              <button
                type="button" onClick={() => setTiketBaru(null)} data-testid="antrian-tutup-cetak"
                className="h-8 px-3 rounded-lg border border-slate-300 text-sm text-slate-700"
              >
                Tutup
              </button>
            </div>
            <TiketPrint
              tiket={tiketBaru}
              pengaturan={tiketBaru._cetak || {}}
              antreanDiDepan={tiketBaru._antrean_di_depan}
              onSelesaiCetak={() => catatCetak(tiketBaru)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function Kartu({ label, nilai, testId }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <p className="text-xs text-slate-500">{label}</p>
      <p className="text-2xl font-semibold text-slate-900" data-testid={testId}>{nilai}</p>
    </div>
  );
}

function PapanAntrean({ judul, items, testId, aksi = null }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid={testId}>
      <p className="text-sm font-medium text-slate-800 mb-2">{judul}</p>
      {items.length === 0 ? (
        <p className="text-xs text-slate-400">Kosong</p>
      ) : (
        <ul className="space-y-1.5">
          {items.map((t) => (
            <li key={t.id} className="flex items-center justify-between gap-2 text-sm">
              <span>
                <b>{t.nomor_antrian}</b>
                <span className="text-xs text-slate-500 ml-2">
                  {t.fl_nama || t.cabang}{t.hasil ? ` · ${t.hasil}` : ""}
                </span>
              </span>
              {aksi && (
                <button
                  type="button" onClick={() => aksi(t)} data-testid={`antrian-lepas-${t.id}`}
                  className="h-7 px-2 rounded-md border border-amber-300 text-xs text-amber-800 hover:bg-amber-50"
                >
                  Lepas
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Kolom teks yang menyimpan saat blur/Enter — pengaturan tiket jarang diubah,
 *  jadi tidak perlu tombol Simpan tersendiri per baris. */
function BarisTeks({ label, nilai, onSimpan, testId }) {
  const [teks, setTeks] = useState(nilai || "");
  useEffect(() => { setTeks(nilai || ""); }, [nilai]);
  return (
    <label className="block text-sm text-slate-700">
      {label}
      <input
        value={teks} onChange={(e) => setTeks(e.target.value)}
        onBlur={() => { if (teks !== (nilai || "")) onSimpan(teks); }}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
        data-testid={testId}
        className="mt-1 w-full h-9 px-3 rounded-lg border border-slate-300 text-sm"
      />
    </label>
  );
}

/** Kartu "Wajib scan sebelum Input Nota" (brief 15 Sep G5/G6).
 *
 *  Dilihat semua pemegang tab Pengaturan, tapi hanya superadmin/admin yang
 *  bisa MENGUBAH (keputusan owner 3). Rincian per jabatan/akun cuma diambil
 *  untuk mereka — endpoint `opsi-wajib-scan` menolak role lain dengan 403,
 *  jadi memanggilnya untuk semua orang hanya melahirkan galat di layar.
 *
 *  Angka "ditegakkan" dan "sebab" datang dari SERVER, bukan dihitung di sini:
 *  memilih jabatan tidak otomatis berarti aturannya berlaku (karyawan tanpa
 *  akun, akun tanpa izin antrian.create, admin yang dikecualikan), dan layar
 *  yang menebak sendiri akan melapor "aman" padahal tidak ada yang dijaga.
 */
function KartuWajibScan({ pengaturan, bolehUbah, bolehBeriIzin, alasanOpsi, bolehAturAlasan, onTersimpan }) {
  const [opsi, setOpsi] = useState(null);
  const [galatOpsi, setGalatOpsi] = useState("");
  const [pilihJabatan, setPilihJabatan] = useState([]);
  const [pilihAkun, setPilihAkun] = useState([]);
  const [ringkasan, setRingkasan] = useState(null);
  const [cariAkun, setCariAkun] = useState("");
  const [busy, setBusy] = useState(false);
  const [hasilIzin, setHasilIzin] = useState(null);

  const muatOpsi = useCallback(() => {
    if (!bolehUbah) return;
    setGalatOpsi("");
    api.get("/antrian/opsi-wajib-scan")
      .then((r) => {
        const d = r.data || {};
        setOpsi(d);
        setPilihJabatan((d.jabatan || []).filter((j) => j.terpilih).map((j) => j.jabatan_dasar));
        setPilihAkun((d.akun || []).filter((a) => a.terpilih).map((a) => a.id));
        setRingkasan(d.ringkasan_penegakan || null);
      })
      .catch((e) => setGalatOpsi(pesanGalat(e, "Gagal memuat daftar jabatan & akun")));
  }, [bolehUbah]);
  useEffect(() => { muatOpsi(); }, [muatOpsi]);

  const jabatanTersimpan = pengaturan?.wajib_scan_jabatan || [];
  const akunTersimpan = pengaturan?.wajib_scan_akun || [];
  const daftarJabatan = useMemo(() => opsi?.jabatan || [], [opsi]);
  const daftarAkun = useMemo(() => opsi?.akun || [], [opsi]);

  // Ejaan diambil dari data karyawan yang ADA, bukan ditulis tangan — jabatan
  // yang diketik beda satu huruf tidak cocok dengan siapa pun dan aturannya
  // diam-diam tidak berlaku.
  const ejaanDiData = daftarJabatan
    .filter((j) => (Number(j.jumlah_karyawan) || 0) > 0)
    .map((j) => j.jabatan_dasar);

  const akunTampil = useMemo(() => {
    const q = cariAkun.trim().toLowerCase();
    if (!q) return daftarAkun;
    return daftarAkun.filter((a) => [a.nama, a.email, a.jabatan, a.cabang, a.role]
      .some((v) => String(v || "").toLowerCase().includes(q)));
  }, [daftarAkun, cariAkun]);

  // Kandidat "berikan izin" dibatasi akun yang DIPILIH dan yang menurut server
  // gagal ditegakkan KARENA tidak punya izin antrian.create. Akun yang gagal
  // karena sebab lain (mis. tidak tertaut karyawan) tidak tertolong oleh izin.
  const kandidatIzin = daftarAkun.filter((a) => pilihAkun.includes(a.id)
    && String(a.sebab || "").toLowerCase().includes("antrian.create"));

  const toggle = (daftar, setDaftar, nilai) => {
    setDaftar(daftar.includes(nilai) ? daftar.filter((x) => x !== nilai) : [...daftar, nilai]);
  };

  const simpan = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/antrian/pengaturan", {
        wajib_scan_jabatan: pilihJabatan, wajib_scan_akun: pilihAkun,
      });
      toast.success(data.message || "Daftar wajib scan tersimpan");
      if (data.ringkasan_penegakan) setRingkasan(data.ringkasan_penegakan);
      muatOpsi();
      if (onTersimpan) onTersimpan();
    } catch (e) {
      toast.error(pesanGalat(e, "Gagal menyimpan daftar wajib scan"));
    } finally {
      setBusy(false);
    }
  };

  const beriIzin = async () => {
    if (kandidatIzin.length === 0) return;
    if (!window.confirm(`Berikan izin scan antrian (antrian.create) ke ${kandidatIzin.length} akun?\n`
      + kandidatIzin.map((a) => `- ${a.nama}`).join("\n"))) return;
    setBusy(true);
    try {
      const { data } = await api.post("/antrian/berikan-izin-scan", {
        user_ids: kandidatIzin.map((a) => a.id),
      });
      toast.success(data.message || "Izin scan diberikan");
      setHasilIzin({ diberikan: data.diberikan || [], dilewati: data.dilewati || [] });
      muatOpsi();
    } catch (e) {
      toast.error(pesanGalat(e, "Gagal memberikan izin scan"));
    } finally {
      setBusy(false);
    }
  };

  const tidakDitegakkan = ringkasan?.tidak_ditegakkan || [];

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3"
         data-testid="antrian-wajib-scan-kartu">
      <p className="text-sm font-medium text-slate-800 flex items-center gap-2">
        <ShieldCheck className="w-4 h-4" /> Wajib scan sebelum Input Nota
      </p>
      <p className="text-sm text-slate-600">
        Karyawan dengan jabatan atau akun yang dipilih di sini wajib memindai nomor antrian pelanggan
        sebelum membuat nota. Satu tiket boleh dipakai untuk lebih dari satu nota oleh FL yang sama
        di hari yang sama.
      </p>

      {!bolehUbah ? (
        <div className="space-y-1 text-sm text-slate-700" data-testid="antrian-wajib-baca-saja">
          <p>
            Jabatan wajib scan:{" "}
            {jabatanTersimpan.length ? <b>{jabatanTersimpan.join(", ")}</b>
              : <span className="text-slate-400">belum ada</span>}
          </p>
          <p>Akun yang dipilih per orang: <b>{akunTersimpan.length}</b></p>
          <p className="text-xs text-slate-500">
            Hanya superadmin dan admin yang bisa mengubah daftar ini.
          </p>
        </div>
      ) : (
        <>
          {galatOpsi && (
            <p className="text-sm text-rose-700" data-testid="antrian-wajib-galat">{galatOpsi}</p>
          )}
          {!opsi && !galatOpsi && <LoadingState />}
          {opsi && (
            <>
              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-700">Per jabatan</p>
                <p className="text-xs text-slate-500">
                  Nama jabatan diambil dari data karyawan.
                  {ejaanDiData.length > 0 && <> Ejaan di data: {ejaanDiData.join(", ")}</>}
                </p>
                <p className="text-xs text-amber-700">
                  Aturan jabatan tidak berlaku untuk akun admin/superadmin; pilih per akun kalau perlu.
                </p>
                <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                  {daftarJabatan.length === 0 && (
                    <li className="px-3 py-2 text-xs text-slate-400">Belum ada data jabatan karyawan.</li>
                  )}
                  {daftarJabatan.map((j) => {
                    // Perbandingan KETAT: server lama yang belum mengirim medan
                    // ini tidak boleh membuat semua baris tampak putus.
                    const putus = j.jumlah_cocok_sekarang === 0;
                    return (
                      <li key={j.jabatan_dasar} className="px-3 py-2 text-sm">
                        <label className="flex flex-wrap items-center gap-2">
                          <input
                            type="checkbox"
                            checked={pilihJabatan.includes(j.jabatan_dasar)}
                            onChange={() => toggle(pilihJabatan, setPilihJabatan, j.jabatan_dasar)}
                            data-testid={`antrian-wajib-jabatan-${slugJabatan(j.jabatan_dasar)}`}
                          />
                          <span className={putus ? "font-medium text-rose-700" : "font-medium text-slate-800"}>
                            {j.jabatan_dasar}
                          </span>
                          <span className="text-xs text-slate-500">
                            {j.jumlah_karyawan || 0} karyawan · {j.jumlah_punya_akun || 0} punya akun
                            · {j.jumlah_ditegakkan || 0} ditegakkan
                          </span>
                        </label>
                        {putus && (
                          <p className="text-xs text-rose-700 mt-0.5 ml-6">
                            Tidak cocok dengan data karyawan mana pun
                          </p>
                        )}
                        {(j.mirip || []).length > 0 && (
                          <p className="text-xs text-slate-500 mt-0.5 ml-6">
                            Mirip dengan: {j.mirip.join(", ")}
                          </p>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium text-slate-700">Per akun</p>
                <div className="relative">
                  <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-2.5" />
                  <input
                    value={cariAkun} onChange={(e) => setCariAkun(e.target.value)}
                    placeholder="Cari nama, email, jabatan, cabang…"
                    data-testid="antrian-wajib-akun-cari"
                    className="w-full h-9 pl-8 pr-3 rounded-lg border border-slate-300 text-sm"
                  />
                </div>
                <div className="overflow-x-auto rounded-lg border border-slate-200 max-h-80 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600 text-xs">
                      <tr>
                        <th className="px-3 py-2 w-8"></th>
                        <th className="text-left px-3 py-2">Akun</th>
                        <th className="text-left px-3 py-2">Jabatan · cabang</th>
                        <th className="text-left px-3 py-2">Sebab</th>
                      </tr>
                    </thead>
                    <tbody>
                      {akunTampil.length === 0 && (
                        <tr><td colSpan={4} className="px-3 py-4 text-center text-xs text-slate-400">Tidak ada akun</td></tr>
                      )}
                      {akunTampil.map((a) => (
                        <tr key={a.id} className="border-t border-slate-100 align-top">
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              checked={pilihAkun.includes(a.id)}
                              onChange={() => toggle(pilihAkun, setPilihAkun, a.id)}
                              data-testid={`antrian-wajib-akun-${a.id}`}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <p className="text-slate-800">{a.nama || "-"}</p>
                            <p className="text-xs text-slate-500 break-all">{a.email} · {a.role}</p>
                          </td>
                          <td className="px-3 py-2 text-xs text-slate-600">
                            {a.jabatan || "-"} · {a.cabang || "-"}
                            {a.tertaut_karyawan === false && (
                              <span className="block text-amber-700">tidak tertaut data karyawan</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-xs">
                            {a.ditegakkan ? (
                              <span className="text-emerald-700">ditegakkan</span>
                            ) : a.sebab ? (
                              <span className="text-rose-700">{a.sebab}</span>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {ringkasan && (
        <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-sm"
             data-testid="antrian-wajib-ringkasan">
          <p className="text-slate-800">
            <b>{ringkasan.ditegakkan || 0}</b> ditegakkan · <b>{tidakDitegakkan.length}</b> tidak ditegakkan
          </p>
          {tidakDitegakkan.length > 0 && (
            <ul className="mt-1 text-xs text-rose-700 space-y-0.5">
              {tidakDitegakkan.map((t, i) => (
                <li key={`${t.nama}-${i}`}>{t.nama} — {t.sebab}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {bolehUbah && opsi && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button" onClick={simpan} disabled={busy}
            data-testid="antrian-wajib-simpan"
            className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-slate-900 text-white text-sm disabled:opacity-50"
          >
            <Save className="w-4 h-4" /> Simpan daftar wajib scan
          </button>
          {bolehBeriIzin && (
            <button
              type="button" onClick={beriIzin} disabled={busy || kandidatIzin.length === 0}
              data-testid="antrian-berikan-izin-scan"
              title={kandidatIzin.length === 0
                ? "Tidak ada akun terpilih yang gagal ditegakkan karena tanpa izin antrian.create" : undefined}
              className="inline-flex items-center gap-2 h-9 px-4 rounded-lg border border-slate-300 text-sm text-slate-700 disabled:opacity-50"
            >
              <KeyRound className="w-4 h-4" /> Berikan izin scan ke {kandidatIzin.length} akun
            </button>
          )}
        </div>
      )}

      {hasilIzin && (
        <div className="text-xs space-y-1" data-testid="antrian-izin-scan-hasil">
          {hasilIzin.diberikan.length > 0 && (
            <p className="text-emerald-700">
              Diberikan: {hasilIzin.diberikan.map((d) => d.nama).join(", ")}
            </p>
          )}
          {hasilIzin.dilewati.length > 0 && (
            <ul className="text-amber-700">
              {hasilIzin.dilewati.map((d) => <li key={d.id}>Dilewati: {d.nama} — {d.sebab}</li>)}
            </ul>
          )}
        </div>
      )}

      <div className="border-t border-slate-100 pt-3 space-y-1" data-testid="antrian-wajib-alasan-daftar">
        <p className="text-sm font-medium text-slate-700">Alasan tidak jadi membeli yang aktif</p>
        {(alasanOpsi || []).length === 0 ? (
          <p className="text-xs text-slate-400">Belum ada / tidak bisa dimuat.</p>
        ) : (
          <ul className="flex flex-wrap gap-1.5">
            {alasanOpsi.map((a) => (
              <li key={a.kode} className="px-2 py-0.5 rounded-full bg-slate-100 text-xs text-slate-700">{a.label}</li>
            ))}
          </ul>
        )}
        {bolehAturAlasan && (
          <Link to="/loss-customer" data-testid="antrian-atur-alasan-link"
                className="inline-block text-xs text-emerald-700 font-medium underline">
            Atur di Loss Customer
          </Link>
        )}
      </div>
    </div>
  );
}
